
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { toast } from "react-hot-toast";
import { FiSearch, FiBookOpen, FiRefreshCw, FiUsers } from "react-icons/fi";
import { baseApiURL } from "../../baseUrl";

const StudentData = () => {
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [bookQuery, setBookQuery] = useState("");
  const [bookResults, setBookResults] = useState([]);
  const [booksToAssign, setBooksToAssign] = useState([]);
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [booksToReturn, setBooksToReturn] = useState(() => new Set());
  const [bookCheckQuery, setBookCheckQuery] = useState("");
  const [bookCheckResults, setBookCheckResults] = useState([]);
  const [studentsWithBook, setStudentsWithBook] = useState([]);
  const [showBookCheck, setShowBookCheck] = useState(false);
  const [borrowerSummary, setBorrowerSummary] = useState([]);
  const [borrowerMeta, setBorrowerMeta] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activePanel, setActivePanel] = useState("issue");

  const fetchBorrowerSummary = () => {
    setSummaryLoading(true);
    axios
      .get(`${baseApiURL()}/library/borrowerSummary`)
      .then((res) => {
        if (res.data.success) {
          setBorrowerSummary(res.data.borrowers || []);
          setBorrowerMeta(res.data.meta || null);
        }
      })
      .catch((error) =>
        toast.error(error.response?.data?.message || "Unable to load borrower insights")
      )
      .finally(() => setSummaryLoading(false));
  };

  useEffect(() => {
    fetchBorrowerSummary();
  }, []);

  const loadIssuedBooks = (enrollmentNo) => {
    if (!enrollmentNo) return;
    axios
      .get(`${baseApiURL()}/student/details/getDetailsByEnrollment`, {
        params: { enrollmentNo },
      })
      .then((res) => {
        const issued =
          res.data.user?.[0]?.books?.filter((book) => book.status === "issued") || [];
        setIssuedBooks(issued);
        setBooksToReturn(new Set());
      })
      .catch((error) => console.error(error));
  };

  const fetchStudents = useMemo(
    () =>
      debounce(async (query) => {
        if (!query) {
          setStudentResults([]);
          return;
        }
        try {
          const res = await axios.get(`${baseApiURL()}/student/details/search`, {
            params: { query },
          });
          setStudentResults(res.data.students || []);
        } catch (error) {
          console.error(error);
        }
      }, 300),
    []
  );

  const fetchBooks = useMemo(
    () =>
      debounce(async (query) => {
        if (!query) {
          setBookResults([]);
          return;
        }
        try {
          const res = await axios.get(`${baseApiURL()}/library/search`, {
            params: { query },
          });
          setBookResults(res.data.books || []);
        } catch (error) {
          console.error(error);
        }
      }, 300),
    []
  );

  const checkBookBorrowers = useMemo(
    () =>
      debounce(async (query) => {
        if (!query) {
          setBookCheckResults([]);
          setStudentsWithBook([]);
          return;
        }
        try {
          const res = await axios.get(`${baseApiURL()}/library/search`, {
            params: { query },
          });
          const matches = res.data.books || [];
          setBookCheckResults(matches);
          if (matches.length > 0) {
            const bookId = matches[0]._id;
            const studentRes = await axios.get(
              `${baseApiURL()}/student/details/findByBook`,
              { params: { bookId } }
            );
            setStudentsWithBook(studentRes.data.students || []);
          } else {
            setStudentsWithBook([]);
          }
        } catch (error) {
          console.error(error);
          setStudentsWithBook([]);
        }
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      fetchStudents.cancel();
      fetchBooks.cancel();
      checkBookBorrowers.cancel();
    };
  }, [fetchStudents, fetchBooks, checkBookBorrowers]);
  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setStudentQuery(`${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`.trim());
    setStudentResults([]);
    setBooksToAssign([]);
    setBookQuery("");
    setBookResults([]);
    loadIssuedBooks(student.enrollmentNo);
  };

  const toggleAssignBook = (book) => {
    setBooksToAssign((prev) => {
      if (prev.find((item) => item._id === book._id)) {
        return prev.filter((item) => item._id !== book._id);
      }
      return [...prev, book];
    });
  };

  const removeAssignedBook = (bookId) => {
    setBooksToAssign((prev) => prev.filter((item) => item._id !== bookId));
  };

  const toggleReturnSelection = (bookId) => {
    if (!bookId) return;
    setBooksToReturn((prev) => {
      const next = new Set(prev);
      if (next.has(bookId)) {
        next.delete(bookId);
      } else {
        next.add(bookId);
      }
      return next;
    });
  };

  const assignBooks = async () => {
    if (!selectedStudent) {
      toast.error("Select a student first");
      return;
    }
    if (booksToAssign.length === 0) {
      toast.error("Choose at least one book to assign");
      return;
    }
    try {
      toast.loading("Assigning books...");
      const res = await axios.post(`${baseApiURL()}/student/details/assignBooks`, {
        enrollmentNo: selectedStudent.enrollmentNo,
        bookIds: booksToAssign.map((book) => book._id),
      });
      toast.dismiss();
      if (res.data.success) {
        toast.success(res.data.message || "Books assigned");
        setBooksToAssign([]);
        setBookQuery("");
        setBookResults([]);
        loadIssuedBooks(selectedStudent.enrollmentNo);
        fetchBorrowerSummary();
      } else {
        toast.error(res.data.message || "Assignment failed");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.message || "Assignment failed");
    }
  };

  const returnSelectedBooks = async () => {
    if (!selectedStudent) {
      toast.error("Select a student first");
      return;
    }
    if (booksToReturn.size === 0) {
      toast.error("Select books to return");
      return;
    }
    const ids = Array.from(booksToReturn);
    const confirmReturn = window.confirm(
      `Return ${ids.length} book(s) from ${selectedStudent.firstName}?`
    );
    if (!confirmReturn) return;
    try {
      toast.loading("Processing return...");
      const res = await axios.post(`${baseApiURL()}/student/details/returnBooks`, {
        enrollmentNo: selectedStudent.enrollmentNo,
        bookIds: ids,
      });
      toast.dismiss();
      if (res.data.success) {
        toast.success(res.data.message || "Books returned");
        loadIssuedBooks(selectedStudent.enrollmentNo);
        setBooksToReturn(new Set());
        fetchBorrowerSummary();
      } else {
        toast.error(res.data.message || "Return failed");
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.message || "Return failed");
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString();
  };
  const renderIssuePanel = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
            <FiUsers className="text-indigo-600 dark:text-indigo-400" /> Search Student
          </h2>
          <button
            type="button"
            onClick={() => {
              setSelectedStudent(null);
              setStudentQuery("");
              setBooksToAssign([]);
              setIssuedBooks([]);
              setBooksToReturn(new Set());
            }}
            className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
          >
            Clear
          </button>
        </div>
        <div className="relative">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={studentQuery}
            onChange={(e) => {
              setStudentQuery(e.target.value);
              fetchStudents(e.target.value);
            }}
            placeholder="Search by name or enrollment number"
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {studentResults.length > 0 && (
            <ul className="absolute z-10 mt-2 w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl max-h-60 overflow-auto shadow-lg divide-y divide-slate-100 dark:divide-slate-800">
              {studentResults.map((student) => (
                <li
                  key={student._id}
                  onClick={() => handleStudentSelect(student)}
                  className="px-4 py-2.5 text-xs text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition font-medium"
                >
                  {student.firstName} {student.middleName} {student.lastName} ({student.enrollmentNo})
                </li>
              ))}
            </ul>
          )}
        </div>
        {selectedStudent && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <p className="text-[10px] font-bold uppercase text-slate-400">Name</p>
              <p className="font-bold text-slate-900 dark:text-white mt-0.5">
                {selectedStudent.firstName} {selectedStudent.middleName} {selectedStudent.lastName}
              </p>
            </div>
            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <p className="text-[10px] font-bold uppercase text-slate-400">Enrollment</p>
              <p className="font-bold text-slate-900 dark:text-white mt-0.5 font-mono">{selectedStudent.enrollmentNo}</p>
            </div>
            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <p className="text-[10px] font-bold uppercase text-slate-400">Branch</p>
              <p className="font-bold text-slate-900 dark:text-white mt-0.5">{selectedStudent.branch}</p>
            </div>
            <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
              <p className="text-[10px] font-bold uppercase text-slate-400">Semester</p>
              <p className="font-bold text-slate-900 dark:text-white mt-0.5">Sem {selectedStudent.semester}</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
        <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
          <FiBookOpen className="text-indigo-600 dark:text-indigo-400" /> Pick Books
        </h2>
        <div className="relative">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={bookQuery}
            onChange={(e) => {
              setBookQuery(e.target.value);
              fetchBooks(e.target.value);
            }}
            placeholder="Search by title, code, or author"
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {bookResults.length > 0 && (
            <ul className="absolute z-10 mt-2 w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl max-h-64 overflow-auto shadow-lg divide-y divide-slate-100 dark:divide-slate-800">
              {bookResults.map((book) => {
                const isSelected = booksToAssign.find((item) => item._id === book._id);
                const available =
                  (book.quantity || 0) - (book.issuedCount || 0);
                return (
                  <li
                    key={book._id}
                    onClick={() => toggleAssignBook(book)}
                    className={`px-4 py-2.5 text-xs cursor-pointer transition ${
                      isSelected
                        ? "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-bold"
                        : "text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span>{book.bookName}</span>
                      <span className="text-[11px] text-slate-400">
                        Available {available}/{book.quantity}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {booksToAssign.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {booksToAssign.map((book) => (
              <span
                key={book._id}
                className="px-3 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold flex items-center gap-2"
              >
                {book.bookName}
                <button
                  type="button"
                  onClick={() => removeAssignedBook(book._id)}
                  className="text-indigo-500 hover:text-indigo-800 leading-none cursor-pointer"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2.5 pt-1">
          <button
            type="button"
            onClick={assignBooks}
            disabled={!selectedStudent || booksToAssign.length === 0}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition shadow-xs cursor-pointer"
          >
            Assign Selected
          </button>
          <button
            type="button"
            onClick={returnSelectedBooks}
            disabled={!selectedStudent || booksToReturn.size === 0}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition shadow-xs cursor-pointer"
          >
            Return Selected
          </button>
          <button
            type="button"
            onClick={() => setShowBookCheck(true)}
            className="px-4 py-2 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            Who borrowed this book?
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Currently Issued</h2>
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
            {issuedBooks.length} book{issuedBooks.length === 1 ? "" : "s"}
          </span>
        </div>
        {issuedBooks.length === 0 ? (
          <p className="text-xs text-slate-400">No active issues for this student.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-center w-16">Return</th>
                  <th className="px-4 py-3 text-left">Book</th>
                  <th className="px-4 py-3 text-left">Author</th>
                  <th className="px-4 py-3 text-left">Issue Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                {issuedBooks.map((record) => {
                  const id = record.bookId?._id || record.bookId;
                  return (
                    <tr key={`${id}-${record.issueDate}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={booksToReturn.has(id)}
                          onChange={() => toggleReturnSelection(id)}
                          className="h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-700"
                        />
                      </td>
                      <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                        {record.bookId?.bookName || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {record.bookId?.author || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{formatDate(record.issueDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderInsightsPanel = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Titles</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
            {borrowerMeta?.totalTitles ?? 0}
          </p>
        </div>
        <div className="p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Copies</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
            {borrowerMeta?.totalCopies ?? 0}
          </p>
        </div>
        <div className="p-4 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800/60 rounded-xl">
          <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Currently Issued</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">
            {borrowerMeta?.totalIssued ?? 0}
          </p>
        </div>
        <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-800/60 rounded-xl">
          <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Available</p>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1">
            {borrowerMeta?.totalAvailable ?? 0}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
            <FiUsers className="text-indigo-600 dark:text-indigo-400" /> Active Borrowers
          </h2>
          <button
            type="button"
            onClick={fetchBorrowerSummary}
            className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
        {summaryLoading ? (
          <p className="text-xs text-slate-400">Loading summary...</p>
        ) : borrowerSummary.length === 0 ? (
          <p className="text-xs text-slate-400">No active borrowers at the moment.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Student</th>
                  <th className="px-4 py-3 text-left">Enrollment</th>
                  <th className="px-4 py-3 text-left">Branch</th>
                  <th className="px-4 py-3 text-left">Issued</th>
                  <th className="px-4 py-3 text-left">Books</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                {borrowerSummary.map((borrower) => (
                  <tr key={borrower.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                    <td className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                      {borrower.name || "Unknown"}
                    </td>
                    <td className="px-4 py-3 font-mono">{borrower.enrollmentNo}</td>
                    <td className="px-4 py-3">
                      {borrower.branch} - Sem {borrower.semester}
                    </td>
                    <td className="px-4 py-3 text-indigo-600 dark:text-indigo-400 font-bold">
                      {borrower.currentIssuedCount}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {borrower.issuedBooks
                        .map((book) => book.bookName)
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookCheckModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Who borrowed this book?</h3>
          <button
            type="button"
            onClick={() => {
              setShowBookCheck(false);
              setBookCheckQuery("");
              setBookCheckResults([]);
              setStudentsWithBook([]);
            }}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xs font-bold cursor-pointer"
          >
            Close
          </button>
        </div>
        <div className="relative">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={bookCheckQuery}
            onChange={(e) => {
              setBookCheckQuery(e.target.value);
              checkBookBorrowers(e.target.value);
            }}
            placeholder="Search by title or author"
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        {bookCheckResults.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Matching Books</p>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              {bookCheckResults.map((book) => (
                <li key={book._id} className="px-4 py-2.5 text-xs text-slate-800 dark:text-slate-200 font-medium">
                  {book.bookName} - {book.author} (
                  {(book.quantity || 0) - (book.issuedCount || 0)}/{book.quantity} available)
                </li>
              ))}
            </ul>
          </div>
        )}
        {studentsWithBook.length > 0 ? (
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Borrowers</p>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              {studentsWithBook.map((student) => (
                <li key={student._id} className="px-4 py-2.5 text-xs text-slate-800 dark:text-slate-200">
                  <p className="font-bold text-slate-900 dark:text-white">
                    {student.firstName} {student.middleName} {student.lastName}
                  </p>
                  <p className="text-slate-400 text-[11px] font-mono">{student.enrollmentNo}</p>
                  <p className="text-slate-400 text-[11px]">{student.branch}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          bookCheckQuery && (
            <p className="text-xs text-slate-400">No active borrowers for this title.</p>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
        <div className="bento-header-banner flex flex-wrap gap-4 justify-between items-center">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Library Issue Desk</h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
              Assign & return books, verify borrowers, and monitor activity.
            </p>
          </div>
          <div className="flex space-x-1.5 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
            {["issue", "insights"].map((panel) => (
              <button
                key={panel}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition cursor-pointer ${
                  activePanel === panel
                    ? "bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
                onClick={() => setActivePanel(panel)}
              >
                {panel === "issue" ? "Issue / Return" : "Borrower Insights"}
              </button>
            ))}
          </div>
        </div>
        <div className="p-6">
          {activePanel === "issue" ? renderIssuePanel() : renderInsightsPanel()}
        </div>
      </div>
      {showBookCheck && renderBookCheckModal()}
    </div>
  );
};

export default StudentData;
